import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../i18n'
import { trackPricingEvent, trackCheckoutStart } from '../utils/analytics'
import { useAuth, authFetch } from '../utils/auth'
import SEO from '../components/SEO'
import './PricingPage.css'

type BillingCycle = 'monthly' | 'annual'

interface PlanFeature {
  text: string
  included: boolean
}

interface PricingPlan {
  id: string
  name: string
  monthlyPrice: number
  annualPrice: number
  description: string
  features: PlanFeature[]
  cta: string
  popular?: boolean
}

// Plan pricing in cents (must match backend PLAN_PRICING)
const PLAN_PRICING: Record<string, Record<string, number>> = {
  basic: { monthly: 2900, annual: 7900 },
  pro: { monthly: 6900, annual: 19900 },
}

interface OrderResponse {
  order_id: string
  status: string
  checkout_url: string | null
}

function PricingPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual')
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')

  const plans: PricingPlan[] = [
    {
      id: 'free',
      name: t('pricingFreePlan'),
      monthlyPrice: 0,
      annualPrice: 0,
      description: t('pricingFreeDesc'),
      features: [
        { text: t('pricingFreeFeature1'), included: true },
        { text: t('pricingFreeFeature2'), included: true },
        { text: t('pricingFreeFeature3'), included: true },
        { text: t('pricingFreeFeature4'), included: true },
        { text: t('pricingFreeFeature5'), included: true },
        { text: t('pricingFreeFeature6'), included: true },
      ],
      cta: t('pricingFreeCta'),
    },
    {
      id: 'basic',
      name: t('pricingBasicPlan'),
      monthlyPrice: 29,
      annualPrice: 79,
      description: t('pricingBasicDesc'),
      features: [
        { text: t('pricingBasicFeature1'), included: true },
        { text: t('pricingBasicFeature2'), included: true },
        { text: t('pricingBasicFeature3'), included: true },
        { text: t('pricingBasicFeature4'), included: true },
        { text: t('pricingBasicFeature5'), included: true },
        { text: t('pricingBasicFeature6'), included: true },
      ],
      cta: t('pricingBasicCta'),
    },
    {
      id: 'pro',
      name: t('pricingProPlan'),
      monthlyPrice: 69,
      annualPrice: 199,
      description: t('pricingProDesc'),
      features: [
        { text: t('pricingProFeature1'), included: true },
        { text: t('pricingProFeature2'), included: true },
        { text: t('pricingProFeature3'), included: true },
        { text: t('pricingProFeature4'), included: true },
        { text: t('pricingProFeature5'), included: true },
        { text: t('pricingProFeature6'), included: true },
      ],
      cta: t('pricingProCta'),
      popular: true,
    },
  ]

  const handleBillingToggle = (cycle: BillingCycle) => {
    setBillingCycle(cycle)
    trackPricingEvent('billing_toggle', undefined, cycle)
  }

  const handleSelectPlan = async (plan: PricingPlan) => {
    if (plan.id === 'free') {
      // Free plan = register and start trial
      trackPricingEvent('select_plan', plan.id, billingCycle)
      if (user) {
        navigate('/dashboard')
      } else {
        navigate('/register')
      }
      return
    }

    trackPricingEvent('select_plan', plan.id, billingCycle)

    // Must be logged in to purchase
    if (!user) {
      navigate('/login', { state: { from: '/pricing' } })
      return
    }

    // Create order and redirect to PayPal checkout
    setCheckoutLoading(true)
    setCheckoutError('')

    const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice
    trackCheckoutStart(plan.id, billingCycle, price)

    try {
      const priceCents = PLAN_PRICING[plan.id]?.[billingCycle] ?? price * 100
      const data = await authFetch<OrderResponse>('/orders', {
        method: 'POST',
        body: JSON.stringify({
          plan_id: plan.id,
          billing_cycle: billingCycle,
          price_cents: priceCents,
        }),
      })

      if (data.checkout_url) {
        // Redirect to PayPal subscription approval page
        window.location.href = data.checkout_url
      } else {
        setCheckoutError(t('checkoutError'))
      }
    } catch (err) {
      console.error('Checkout error:', err)
      setCheckoutError(err instanceof Error ? err.message : t('checkoutError'))
    } finally {
      setCheckoutLoading(false)
    }
  }

  const getPrice = (plan: PricingPlan) => {
    if (plan.id === 'free') return '$0'
    return billingCycle === 'annual' 
      ? `$${plan.annualPrice}` 
      : `$${plan.monthlyPrice}`
  }

  const getPriceLabel = (plan: PricingPlan) => {
    if (plan.id === 'free') return ''
    return billingCycle === 'annual' ? t('pricingPerYear') : t('pricingPerMonth')
  }

  const getMonthlyEquivalent = (plan: PricingPlan) => {
    if (plan.id === 'free' || billingCycle === 'monthly') return null
    const monthly = (plan.annualPrice / 12).toFixed(2)
    return `$${monthly}/mo`
  }

  // Determine if this plan is the user's current active plan
  const isCurrentPlan = (planId: string): boolean => {
    if (!user || !user.is_paid) return false
    return user.plan === planId
  }

  // Get the CTA text based on user's current plan
  const getCtaText = (plan: PricingPlan): string => {
    if (checkoutLoading && plan.id !== 'free') return t('checkoutProcessing')
    if (isCurrentPlan(plan.id)) return t('pricingCurrentPlan')
    if (user?.is_paid && plan.id === 'free') return t('pricingFreeCta')
    return plan.cta
  }


  return (
    <div className="pricing-page">
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for Intervio. Start with a free 3-hour trial, then choose Basic (10 hours/month from $79/year) or Pro (30 hours/month from $199/year). All features included."
        path="/pricing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Intervio',
          description: 'AI-powered interview assistant with real-time answer hints, transcription, and screen OCR.',
          brand: { '@type': 'Brand', name: 'Intervio' },
          offers: [
            {
              '@type': 'Offer',
              name: 'Free Trial',
              price: '0',
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              description: '3 hours of full access with all features',
            },
            {
              '@type': 'Offer',
              name: 'Basic Monthly',
              price: '29',
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              description: '10 hours of session time per month',
              priceValidUntil: new Date(new Date().getFullYear() + 1, 0, 1).toISOString().split('T')[0],
            },
            {
              '@type': 'Offer',
              name: 'Basic Annual',
              price: '79',
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              description: '10 hours of session time per month, billed annually',
              priceValidUntil: new Date(new Date().getFullYear() + 1, 0, 1).toISOString().split('T')[0],
            },
            {
              '@type': 'Offer',
              name: 'Pro Monthly',
              price: '69',
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              description: '30 hours of session time per month',
              priceValidUntil: new Date(new Date().getFullYear() + 1, 0, 1).toISOString().split('T')[0],
            },
            {
              '@type': 'Offer',
              name: 'Pro Annual',
              price: '199',
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              description: '30 hours of session time per month, billed annually',
              priceValidUntil: new Date(new Date().getFullYear() + 1, 0, 1).toISOString().split('T')[0],
            },
          ],
        }}
      />
      {/* Hero Section */}
      <section className="pricing-hero section">
        <div className="container">
          <div className="section-title">
            <h1>{t('pricingPageTitle')}</h1>
            <p>{t('pricingPageSubtitle')}</p>
          </div>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button
              className={`billing-option ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => handleBillingToggle('monthly')}
            >
              {t('pricingMonthly')}
            </button>
            <button
              className={`billing-option ${billingCycle === 'annual' ? 'active' : ''}`}
              onClick={() => handleBillingToggle('annual')}
            >
              {t('pricingAnnual')}
              <span className="save-badge">{t('pricingSavePercent')}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-cards section">
        <div className="container">
          <div className="plans-grid">
            {plans.map((plan) => (
              <div 
                key={plan.id} 
                className={`plan-card ${plan.popular ? 'popular' : ''} ${isCurrentPlan(plan.id) ? 'current' : ''}`}
              >
                {isCurrentPlan(plan.id) && (
                  <div className="current-plan-badge">{t('pricingCurrentPlan')}</div>
                )}
                {!isCurrentPlan(plan.id) && plan.popular && (
                  <div className="popular-badge">{t('pricingProBadge')}</div>
                )}
                
                <div className="plan-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="plan-price">
                  <span className="price-amount">{getPrice(plan)}</span>
                  <span className="price-period">{getPriceLabel(plan)}</span>
                  {getMonthlyEquivalent(plan) && (
                    <span className="price-monthly-equiv">
                      {getMonthlyEquivalent(plan)} {t('pricingBilledAnnually')}
                    </span>
                  )}
                </div>

                <ul className="plan-features">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="feature-item">
                      <svg className="feature-check" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature.text}
                    </li>
                  ))}
                </ul>

                <button 
                  className={`plan-cta btn ${isCurrentPlan(plan.id) ? 'btn-current' : plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSelectPlan(plan)}
                  disabled={checkoutLoading || isCurrentPlan(plan.id)}
                >
                  {getCtaText(plan)}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Checkout error banner */}
      {checkoutError && (
        <div className="checkout-error-banner">
          <p>{checkoutError}</p>
          <button onClick={() => setCheckoutError('')}>&times;</button>
        </div>
      )}
    </div>
  )
}

export default PricingPage
